export interface ValidationResult {
  isValid: boolean;
  issues: string[];
}

export interface NodeValidationResult {
  nodeId: string;
  identifier?: string;
  validation: ValidationResult;
  hasDuplicateIdentifier: boolean;
}

export interface FlowValidationResult {
  isValid: boolean;
  invalidNodes: NodeValidationResult[];
  allIssues: string[];
}

// Validate a single node (extracted from CustomNode.tsx)
export const validateNode = (data: any, placeholders: any, globalRegistry: any): ValidationResult => {
  const issues: string[] = [];
  
  // Skip validation for placeholder nodes
  if (data.isPlaceholder) {
    return { isValid: true, issues: [] };
  }
  
  // Decision nodes have different validation rules
  if (data.entityType === "DECISION") {
    // Check if decision data exists
    if (!data.decisionData) {
      issues.push("Decision node has no decision logic configured");
    } else if (Array.isArray(data.decisionData.conditions)) {
      // New model: conditions array
      if (data.decisionData.conditions.length === 0) {
        issues.push("Decision node has no conditions configured");
      } else {
        data.decisionData.conditions.forEach((cond: any, idx: number) => {
          if (!cond.variable || cond.variable.trim() === "") {
            issues.push(`Condition ${idx + 1} is missing a variable`);
          }
          if (cond.type === "value" && (!cond.value || cond.value.trim() === "")) {
            issues.push(`Condition ${idx + 1} is missing a comparison value`);
          }
        });
      }
      // Check if at least one path is configured
      if (!data.decisionData.truePath && !data.decisionData.falsePath) {
        issues.push("Decision node has no paths configured");
      }
    } else if (data.decisionData.condition) {
      // Old model: single condition
      if (!data.decisionData.condition.variable || data.decisionData.condition.variable.trim() === "") {
        issues.push("Decision node is missing a variable");
      }
      if (data.decisionData.condition.type === "value" && 
          (!data.decisionData.condition.value || data.decisionData.condition.value.trim() === "")) {
        issues.push("Decision node is missing a comparison value");
      }
      if (!data.decisionData.truePath && !data.decisionData.falsePath) {
        issues.push("Decision node has no paths configured");
      }
    }
    return { isValid: issues.length === 0, issues };
  }
  
  // Check for empty content - but allow empty content for BOT nodes with tool calls
  const hasValidToolCalls = data.entityType === "BOT" && data.toolCalls && data.toolCalls.length > 0 && data.toolCalls.some((tc: any) => tc.name && tc.name.trim());
  if ((!data.content || data.content.trim() === "") && !hasValidToolCalls) {
    issues.push("Node content is empty");
  }
  
  // Check for undefined placeholders in content
  if (data.content) {
    const placeholderRegex = /\{([^}]+)\}/g;
    let match;
    while ((match = placeholderRegex.exec(data.content)) !== null) {
      const placeholderName = match[1];
      const hasLocalDefinition = placeholders[placeholderName];
      const hasGlobalDefinition = globalRegistry && globalRegistry[placeholderName];
      
      if (!hasLocalDefinition && !hasGlobalDefinition) {
        issues.push(`Placeholder "${placeholderName}" is not defined`);
      } else {
        // Check if placeholder has examples - prioritize global registry
        const hasGlobalDefinition = globalRegistry[placeholderName];
        let definition;
        if (hasGlobalDefinition && hasGlobalDefinition.values && hasGlobalDefinition.values.length > 0) {
          // Use global definition if it exists and has examples
          definition = hasGlobalDefinition;
        } else if (hasGlobalDefinition) {
          // Use global definition even if it has no examples (to show the warning)
          definition = hasGlobalDefinition;
        } else {
          definition = placeholders[placeholderName];
        }
        
        if (definition && (!definition.values || definition.values.length === 0)) {
          issues.push(`Placeholder "${placeholderName}" has no examples`);
        }
      }
    }
  }
  
  return { isValid: issues.length === 0, issues };
};

// Validate all nodes in the flow and return detailed results
export const validateFlow = (
  nodes: any[], 
  globalVariableRegistry: any
): FlowValidationResult => {
  const invalidNodes: NodeValidationResult[] = [];
  const allIssues: string[] = [];
  const placeholderIssues = new Set<string>(); // Track unique placeholder issues

  // Create a map to track duplicate identifiers
  const identifierMap: Record<string, string[]> = {};
  
  // First pass: collect all identifiers
  nodes.forEach(node => {
    if (!node.data.isPlaceholder && node.data.identifier) {
      const identifier = node.data.identifier;
      if (!identifierMap[identifier]) {
        identifierMap[identifier] = [];
      }
      identifierMap[identifier].push(node.id);
    }
  });

  // Second pass: validate each node
  nodes.forEach(node => {
    const placeholders = node.data.placeholders || {};
    const validation = validateNode(node.data, placeholders, globalVariableRegistry);
    
    // Check for duplicate identifier
    const hasDuplicateIdentifier = node.data.identifier && 
      identifierMap[node.data.identifier] && 
      identifierMap[node.data.identifier].length > 1;

    // If node is invalid or has duplicate identifier, add to invalid nodes
    if (!validation.isValid || hasDuplicateIdentifier) {
      // Separate placeholder issues from other node issues
      const nodeSpecificIssues: string[] = [];
      const nodePlaceholderIssues: string[] = [];

      validation.issues.forEach(issue => {
        if (issue.includes("Placeholder \"") && issue.includes("has no examples")) {
          // This is a placeholder issue - add to global set
          placeholderIssues.add(issue);
          nodePlaceholderIssues.push(issue);
        } else if (issue.includes("Placeholder \"") && issue.includes("is not defined")) {
          // This is also a placeholder issue - add to global set
          placeholderIssues.add(issue);
          nodePlaceholderIssues.push(issue);
        } else {
          // This is a node-specific issue
          nodeSpecificIssues.push(issue);
        }
      });

      // Add duplicate identifier issue if present
      if (hasDuplicateIdentifier) {
        nodeSpecificIssues.unshift(`Duplicate identifier "${node.data.identifier}" is used by ${identifierMap[node.data.identifier].length} nodes`);
      }

      // Only add to invalid nodes if there are node-specific issues or placeholder issues affecting this node
      if (nodeSpecificIssues.length > 0 || nodePlaceholderIssues.length > 0) {
        const nodeValidationResult: NodeValidationResult = {
          nodeId: node.id,
          identifier: node.data.identifier,
          validation: {
            isValid: false,
            issues: [...nodeSpecificIssues, ...nodePlaceholderIssues]
          },
          hasDuplicateIdentifier: !!hasDuplicateIdentifier
        };

        invalidNodes.push(nodeValidationResult);
        
        // Add node-specific issues to allIssues with node context
        nodeSpecificIssues.forEach(issue => {
          const nodeLabel = node.data.identifier ? `"${node.data.identifier}"` : `Node ${node.id}`;
          allIssues.push(`${nodeLabel}: ${issue}`);
        });
      }
    }
  });

  // Add unique placeholder issues to allIssues (without node context since they're global)
  placeholderIssues.forEach(issue => {
    allIssues.push(issue);
  });

  return {
    isValid: invalidNodes.length === 0 && placeholderIssues.size === 0,
    invalidNodes,
    allIssues
  };
}; 